import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { ChunkErrorService } from './chunk-error.service';
import { CrashlyticsService } from './crashlytics.service';
import { LoggingService, SeverityLevel } from './logging.service';
import { ToastService } from './toast.service';

export function isChunkLoadingError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message?.toLowerCase() || '';
    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('loading chunk') ||
      message.includes('loading css chunk')
    );
  }
  return false;
}

export type AppErrorType =
  | 'NetworkError'
  | 'AuthError'
  | 'PaymentError'
  | 'DocumentError'
  | 'ValidationError'
  | 'HelocError'
  | 'UnknownError';

export class AppError extends Error {
  showErrorPage: boolean;

  refreshNeeded: boolean;

  displayMessage: string;

  errorType: AppErrorType;

  originalError?: Error;

  constructor(
    message: string,
    options: {
      showErrorPage?: boolean;
      refreshNeeded?: boolean;
      displayMessage?: string;
      errorType?: AppErrorType;
      originalError?: Error;
    } = {}
  ) {
    super(message);
    this.name = 'AppError';
    this.showErrorPage = options.showErrorPage ?? false;
    this.refreshNeeded = options.refreshNeeded ?? false;
    this.displayMessage = options.displayMessage ?? '';
    this.errorType = options.errorType ?? 'UnknownError';
    this.originalError = options.originalError;
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

export class PaymentError extends AppError {
  constructor(message: string, displayMessage?: string) {
    super(message, {
      errorType: 'PaymentError',
      displayMessage: displayMessage ?? '',
    });
    this.name = 'PaymentError';
    Object.setPrototypeOf(this, PaymentError.prototype);
  }
}

export class DocumentError extends AppError {
  constructor(message: string, displayMessage?: string) {
    super(message, {
      errorType: 'DocumentError',
      displayMessage: displayMessage ?? '',
    });
    this.name = 'DocumentError';
    Object.setPrototypeOf(this, DocumentError.prototype);
  }
}

export class AuthError extends AppError {
  constructor(message: string, displayMessage?: string) {
    super(message, {
      errorType: 'AuthError',
      displayMessage: displayMessage ?? '',
      showErrorPage: false,
    });
    this.name = 'AuthError';
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

export class NetworkError extends AppError {
  statusCode?: number;

  constructor(message: string, statusCode?: number, displayMessage?: string) {
    super(message, {
      errorType: 'NetworkError',
      displayMessage: displayMessage ?? '',
    });
    this.name = 'NetworkError';
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

export class HelocError extends AppError {
  operation?: 'draw' | 'payment' | 'eligibility' | 'info';

  constructor(
    message: string,
    options: {
      displayMessage?: string;
      operation?: 'draw' | 'payment' | 'eligibility' | 'info';
      showErrorPage?: boolean;
    } = {}
  ) {
    super(message, {
      errorType: 'HelocError',
      displayMessage: options.displayMessage ?? '',
      showErrorPage: options.showErrorPage ?? false,
    });
    this.name = 'HelocError';
    this.operation = options.operation;
    Object.setPrototypeOf(this, HelocError.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private readonly logger = inject(LoggingService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly env = inject(ENVIRONMENT);
  private readonly translocoService = inject(TranslocoService);

  handleError(error: unknown, severityLevel: SeverityLevel = SeverityLevel.Error): void {
    const normalizedError = this.normalizeError(error);

    this.logger.logException(normalizedError, severityLevel);

    if (normalizedError instanceof AppError) {
      this.handleAppError(normalizedError);
    } else {
      this.handleGenericError(normalizedError);
    }
  }

  private handleAppError(error: AppError): void {
    const displayMessage = error.displayMessage || this.getDefaultErrorMessage(error.errorType);

    if (error.showErrorPage) {
      this.router.navigate(['/error'], {
        state: { prevPage: this.router.url, message: displayMessage },
      });
      return;
    }

    this.toastService.error(displayMessage);

    if (error.refreshNeeded) {
      setTimeout(() => {
        if (Capacitor.isNativePlatform()) {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([this.router.url]);
          });
        } else {
          window.location.reload();
        }
      }, 2000);
    }
  }

  private handleGenericError(error: Error): void {
    const message = this.env.production
      ? this.translocoService.translate('errorsGeneric')
      : error.message || this.translocoService.translate('errorsGeneric');

    this.toastService.error(message);
  }

  private getDefaultErrorMessage(errorType: AppErrorType): string {
    switch (errorType) {
      case 'PaymentError':
        return this.translocoService.translate('servicesErrorsPaymentGeneric');
      case 'DocumentError':
        return this.translocoService.translate('servicesErrorsDocumentNotFound');
      case 'AuthError':
        return this.translocoService.translate('servicesErrorsAuthFailed');
      case 'NetworkError':
        return this.translocoService.translate('servicesErrorsNetworkGeneric');
      case 'HelocError':
        return this.translocoService.translate('servicesErrorsHelocGeneric');
      default:
        return this.translocoService.translate('servicesErrorsGenericDefault');
    }
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    if (error && typeof error === 'object') {
      const errorObj = error as Record<string, unknown>;
      const message = (errorObj['message'] as string) || (errorObj['error'] as string) || 'Unknown error';
      return new Error(message);
    }

    return new Error(this.translocoService.translate('servicesErrorsGenericUnknown'));
  }

  createError(message: string, context: string, originalError?: unknown): AppError {
    const contextMessage = `${context}: ${message}`;
    return new AppError(contextMessage, {
      originalError: originalError instanceof Error ? originalError : undefined,
    });
  }

  handleHttpError(status: number, message: string, url?: string): void {
    const context = url ? `API call to ${url}` : 'API call';

    switch (status) {
      case 401:
      case 403:
        this.handleError(new AuthError(`${context}: ${message}`));
        break;
      case 404:
        this.handleError(
          new AppError(`${context}: Resource not found`, {
            displayMessage: this.translocoService.translate('servicesErrorsResourceNotFound'),
          })
        );
        break;
      case 500:
      case 502:
      case 503:
        this.handleError(
          new NetworkError(
            `${context}: Server error`,
            status,
            this.translocoService.translate('servicesErrorsServerUnavailable')
          )
        );
        break;
      default:
        this.handleError(new NetworkError(`${context}: ${message}`, status));
    }
  }
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);

  handleError(error: unknown): void {
    if (error instanceof Error && error.message?.includes('[ErrorHandler]')) {
      return;
    }

    if (this.isCrossOriginScriptError(error)) {
      return;
    }

    if (this.isBenignThirdPartyError(error)) {
      return;
    }

    if (isChunkLoadingError(error)) {
      this.injector.get(ChunkErrorService).showRefreshDialog();
      return;
    }

    this.errorHandlerService.handleError(error, SeverityLevel.Critical);

    this.reportToCrashlytics(error);
  }

  private reportToCrashlytics(error: unknown): void {
    try {
      const crashlytics = this.injector.get(CrashlyticsService);
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      crashlytics.setCustomKey('currentRoute', this.router.url);
      crashlytics.recordException(normalizedError, 'GlobalErrorHandler');
    } catch {
      // Crashlytics not available, silently ignore
    }
  }

  private isCrossOriginScriptError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message?.toLowerCase() || '';
      return message === 'script error.' || message === 'script error';
    }
    return false;
  }

  private isBenignThirdPartyError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message?.toLowerCase() || '';

      if (message.includes('resizeobserver loop')) {
        return true;
      }

      if (message.includes('cannot perform action before init has completed')) {
        return true;
      }

      if (message.includes('servisbot') && message.includes('not initialized')) {
        return true;
      }
    }
    return false;
  }
}
